import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { fmtTz } from "../lib/time";

export const TimeIssuesCard = () => {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);

  const load = useCallback(() => {
    authAxios.get('/admin/time-issues').then((r) => setIssues(r.data)).catch(() => {});
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (i) => {
    await authAxios.post(`/admin/time-issues/${i.entry_id}/${i.session_id}/resolve`);
    toast.success('Marked as resolved');
    load();
  };

  if (issues.length === 0) return null;

  return (
    <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white mb-8 ring-2 ring-amber-200" data-testid="time-issues-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-heading flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          Time Corrections Requested
          <span className="text-sm font-mono text-amber-600" data-testid="time-issues-count">({issues.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {issues.map((i) => (
          <div key={i.session_id} className="p-3 rounded-xl bg-amber-50 border border-amber-100 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`time-issue-${i.session_id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-stone-900">
                {i.user_name} · {fmtTz(i.date, 'EEE, MMM d')}
                <span className="font-mono text-sm text-stone-500 ml-2">{fmtTz(i.clock_in, 'HH:mm')} – {fmtTz(i.clock_out, 'HH:mm')}</span>
              </p>
              <p className="text-sm text-amber-800 mt-0.5">"{i.note}"</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => navigate('/admin/records')} data-testid={`time-issue-edit-${i.session_id}`}>
                <Pencil className="w-4 h-4 mr-1" /> Edit Hours
              </Button>
              <Button size="sm" onClick={() => resolve(i)} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`time-issue-resolve-${i.session_id}`}>
                <Check className="w-4 h-4 mr-1" /> Resolved
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
